import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { TranslatedCalling } from './common';
import { InstagramPreview } from '../../InstagramPreview';
import { Calendar, Send } from 'lucide-react';
import { Button } from '@artstorefronts/ui';
import { CREATE_SOCIAL_POST } from '../../../graphql/mutations';
import { INTEGRATIONS_QUERY } from '../../../graphql/queries';
import { DateTimePicker } from '../../DateTimePicker';
import { AccountSelector } from '../../AccountSelector';
import { PostNowDialog } from '../../PostNowDialog';
import { toast } from 'sonner';
import { useAuth } from '../../../context/auth';

function ReelResultCard({ result }: { result: any }) {
  const { t } = useTranslation('chat');
  const { token } = useAuth();
  const [showScheduling, setShowScheduling] = useState(false);
  const [scheduledFor, setScheduledFor] = useState<Date | null>(null);
  const [selectedUserIntegration, setSelectedUserIntegration] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPostConfirmDialog, setShowPostConfirmDialog] = useState(false);

  const { data: integrationsData, loading: integrationsLoading } = useQuery(INTEGRATIONS_QUERY, {
    skip: !token,
  });

  const [createSocialPost] = useMutation(CREATE_SOCIAL_POST, {
    onCompleted: () => {
      toast.success(scheduledFor ? t('tool.generate-reel.scheduled') : t('tool.generate-reel.created'));
      setShowScheduling(false);
      setScheduledFor(null);
      setSelectedUserIntegration(null);
      setShowPostConfirmDialog(false);
    },
    onError: (error) => {
      toast.error(t('tool.generate-reel.error-scheduling', { message: error.message }));
      setIsSubmitting(false);
      setShowPostConfirmDialog(false);
    },
  });

  const handleScheduleClick = () => {
    setShowScheduling(true);
  };

  const handleSubmitSchedule = async (postDate: Date): Promise<void> => {
    if (!postDate || !selectedUserIntegration) {
      toast.error(t('tool.common.select-date-and-account'));
      return;
    }

    setIsSubmitting(true);

    try {
      const captionText = `${result?.title || ''}\n\n${result?.content || ''}\n\n${result?.hashtags?.join(' ') || ''}`;

      await createSocialPost({
        variables: {
          input: {
            userIntegrationId: selectedUserIntegration.id,
            content: captionText,
            mediaUrls: [result?.videoUrl || ''],
            scheduledFor: postDate.toISOString(),
            reel: true,
            inputProps: result?.inputProps || undefined,
          },
        },
      });
    } catch (error) {
      console.error('Error scheduling reel:', error);
      setIsSubmitting(false);
    }
  };

  const activeIntegrations =
    integrationsData?.integrations?.filter((integration: any) =>
      integration.userIntegrations?.some((ui: any) => ui.status === 'active')
    ) || [];

  useEffect(() => {
    if (!selectedUserIntegration && !integrationsLoading && activeIntegrations.length > 0) {
      const allUserIntegrations = activeIntegrations.flatMap((integration: any) =>
        integration.userIntegrations.map((ui: any) => ui)
      );

      if (allUserIntegrations.length > 0) {
        setSelectedUserIntegration(allUserIntegrations[0]);
      }
    }
  }, [activeIntegrations, selectedUserIntegration, integrationsLoading]);

  return (
    <div className="my-6 space-y-4">
      <div className="w-[300px]">
        <InstagramPreview
          username={selectedUserIntegration?.metadata?.username || 'artist'}
          profilePicture={selectedUserIntegration?.metadata?.profilePictureUrl}
          images={[]}
          caption={result?.content || ''}
          skipImagePreload={true}
          isReel={true}
          inputProps={result?.inputProps || undefined}
        />

        {token && !showScheduling && (
          <div className="mt-3 flex gap-2">
            <Button
              onClick={handleScheduleClick}
              className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white border-0"
            >
              <Calendar className="h-4 w-4 mr-2" />
              {t('tool.generate-reel.schedule-reel')}
            </Button>
            <Button
              onClick={() => {
                setShowPostConfirmDialog(true);
              }}
              className="bg-gradient-to-br from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white border-0"
            >
              <Send className="h-4 w-4 mr-2" />
              {t('tool.common.post-now')}
            </Button>
          </div>
        )}
      </div>

      {token && showScheduling && (
        <div className="space-y-4 max-w-[400px]">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('tool.common.account')}</label>
            <AccountSelector
              integrations={activeIntegrations}
              loading={integrationsLoading}
              selectedUserIntegration={selectedUserIntegration}
              onSelect={setSelectedUserIntegration}
            />
          </div>

          {activeIntegrations.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('tool.common.date-and-time')}</label>
              <div className="w-full">
                <DateTimePicker date={scheduledFor} onDateChange={setScheduledFor} disablePastDates={true} />
              </div>
            </div>
          )}

          {activeIntegrations.length > 0 && (
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowScheduling(false)}
                className="flex-1 text-sm"
                disabled={isSubmitting}
              >
                {t('tool.common.cancel')}
              </Button>
              <Button
                onClick={() => handleSubmitSchedule(scheduledFor || new Date())}
                disabled={!scheduledFor || !selectedUserIntegration || isSubmitting}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white border-0 text-sm"
              >
                {isSubmitting ? t('tool.common.scheduling') : t('tool.common.schedule')}
              </Button>
            </div>
          )}
        </div>
      )}

      <PostNowDialog
        open={showPostConfirmDialog}
        onOpenChange={setShowPostConfirmDialog}
        onConfirm={() => handleSubmitSchedule(new Date())}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}

export const generate_reel = {
  type: 'acting',
  call: () => <TranslatedCalling i18nKey="tool.generate-reel.creating" />,
  'partial-call': () => <TranslatedCalling i18nKey="tool.generate-reel.generating" />,
  result: ({ toolCall }: any) => <ReelResultCard result={toolCall.result} />,
};
